import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { e6ToText, parseCoordinates } from '@zt/content/geo';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Switch,
  Textarea,
} from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import { TrPlaceFields } from '../lib/tr-place';
import { ConfirmButton } from './Confirm';
import type {
  CompanySalesPoints,
  SalesPointGeocodeOne,
  SalesPointHours,
  SalesPointRow,
  SalesPointWarning,
} from '../lib/types';

/**
 * Bayi kartında "Satış noktası" (F7-37, ADR-145): vitrindeki Satış Noktaları sayfasında görünen işletme bilgisi.
 * Lokasyondan eklenirken ad, il, ilçe ve adres önerilir; yetkili adı ve telefonu önerilmez (KVKK). Konum Google
 * Haritalar'dan kopyalanan koordinat veya bağlantıyla girilir (metin bu tarayıcıda çözülür, dışarı gönderilmez).
 */

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
type Day = (typeof WEEKDAYS)[number];

interface Draft {
  locationId: string | null;
  visible: boolean;
  name: string;
  city: string;
  district: string;
  neighbourhood: string;
  addressLine: string;
  postalCode: string;
  pin: string;
  phone: string;
  /** Gün → { kapalı | açık aralık | bilinmiyor }. */
  hours: Record<Day, { mode: 'unknown' | 'closed' | 'open'; from: string; to: string }>;
  note: string;
}

const emptyHours = (): Draft['hours'] =>
  Object.fromEntries(
    WEEKDAYS.map((d) => [d, { mode: 'unknown', from: '09:00', to: '18:00' }]),
  ) as Draft['hours'];

/** API yanıtında aralıklar dizi dizisi olarak gelir. */
function hoursDraft(h: Partial<Record<Day, string[][]>> | null): Draft['hours'] {
  const out = emptyHours();
  if (!h) return out;
  for (const d of WEEKDAYS) {
    const r = h[d];
    if (!r) continue;
    out[d] = r.length
      ? { mode: 'open', from: r[0]![0] ?? '09:00', to: r[0]![1] ?? '18:00' }
      : { mode: 'closed', from: '09:00', to: '18:00' };
  }
  return out;
}

function hoursBody(h: Draft['hours']): SalesPointHours | null {
  const out: SalesPointHours = {};
  for (const d of WEEKDAYS) {
    const v = h[d];
    if (v.mode === 'closed') out[d] = [];
    else if (v.mode === 'open') out[d] = [[v.from, v.to]];
  }
  return Object.keys(out).length ? out : null;
}

const draftOf = (p: SalesPointRow): Draft => ({
  locationId: p.locationId,
  visible: p.visible,
  name: p.name,
  city: p.city,
  district: p.district,
  neighbourhood: p.neighbourhood ?? '',
  addressLine: p.addressLine ?? '',
  postalCode: p.postalCode ?? '',
  pin: p.point ? `${e6ToText(p.point.latE6)}, ${e6ToText(p.point.lngE6)}` : '',
  phone: p.phone ?? '',
  hours: hoursDraft(p.hours),
  note: p.note ?? '',
});

export function useCompanySalesPoints(companyId: string) {
  return useQuery({
    queryKey: ['company-sales-points', companyId],
    queryFn: () => api.get<CompanySalesPoints>(`/v1/admin/companies/${companyId}/sales-points`),
  });
}

/** İşlem gerektiren uyarılar sarı (kişi adı, onaysız bayi, adres sorunları — F7-42). */
const AMBER_WARNINGS = new Set<SalesPointWarning>([
  'person_name',
  'company_not_approved',
  'address_missing',
  'geocode_not_found',
  'geocode_mismatch',
]);

export function WarningBadges({ warnings }: { warnings: SalesPointWarning[] }) {
  const t = useT();
  return (
    <>
      {warnings.map((w) => (
        <Badge key={w} tone={AMBER_WARNINGS.has(w) ? 'amber' : 'neutral'}>
          {t(`admin.salesPoints.warnings.${w}`)}
        </Badge>
      ))}
    </>
  );
}

export function CompanySalesPointsSection({ companyId }: { companyId: string }) {
  const t = useT();
  const { can } = usePermissions();
  const manage = can('b2b.dealer.manage');
  const q = useCompanySalesPoints(companyId);
  const [editing, setEditing] = useState<{ point: SalesPointRow | null; locationId: string | null } | null>(
    null,
  );
  const remove = useApiMutation((id: string) => api.del(`/v1/admin/sales-points/${id}`), {
    invalidate: [['company-sales-points', companyId], ['sales-points']],
    success: t('admin.salesPoints.deleted'),
  });
  // Adresten konum bul (F7-42): OpenStreetMap'te arar; sonuç uyarı olarak kartta kalır.
  const geocode = useApiMutation(
    (id: string) => api.post<SalesPointGeocodeOne>(`/v1/admin/sales-points/${id}/geocode`),
    {
      invalidate: [['company-sales-points', companyId], ['sales-points']],
      success: (r) => t(`admin.salesPoints.geocodeResult.${r.status}`, { place: r.note ?? '' }),
    },
  );
  const data = q.data;
  return (
    <Section
      title={t('admin.salesPoints.sectionTitle')}
      description={t('admin.salesPoints.sectionLead')}
      action={
        <span className="inline-flex items-center gap-1">
          {manage && (
            <Button size="sm" onClick={() => setEditing({ point: null, locationId: null })}>
              {t('admin.salesPoints.add')}
            </Button>
          )}
          <HelpTip k="dealers.sales_points" />
        </span>
      }
    >
      {!data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div className="space-y-3">
          {data.personNameRisk && (
            <p className="rounded-md border border-amber/30 bg-amber-tint px-3 py-2 text-sm" role="note">
              {t('admin.salesPoints.personNameNote')}
            </p>
          )}
          {data.company.status !== 'approved' && (
            <p className="text-sm text-ink-2">{t('admin.salesPoints.notApprovedNote')}</p>
          )}
          {data.missing && (
            <p className="rounded-md border border-amber/30 bg-amber-tint px-3 py-2 text-sm" role="note">
              {t(`admin.salesPoints.missing.${data.missing}`)}
            </p>
          )}
          {!data.items.length ? (
            <EmptyState title={t('admin.salesPoints.empty')} description={t('admin.salesPoints.emptyLead')} />
          ) : (
            <ul className="divide-y divide-line rounded-md border border-line">
              {data.items.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{p.name}</p>
                    <p className="text-sm text-ink-3">
                      {[p.neighbourhood, p.addressLine, `${p.district}/${p.city}`].filter(Boolean).join(', ')}
                    </p>
                    <p className="text-xs text-ink-3">
                      {p.point
                        ? t(`admin.salesPoints.pinSource.${p.geocode.source ?? 'manual'}`)
                        : t('admin.salesPoints.pinSource.none')}
                      {p.warnings.includes('geocode_mismatch') && p.geocode.note
                        ? ` · ${t('admin.salesPoints.mismatchFound', { place: p.geocode.note })}`
                        : ''}
                    </p>
                  </div>
                  <Badge tone={p.visible ? 'pine' : 'neutral'}>
                    {p.visible ? t('admin.salesPoints.shown') : t('admin.salesPoints.hidden')}
                  </Badge>
                  <WarningBadges warnings={p.warnings} />
                  {manage && (
                    <>
                      {p.geocode.source !== 'manual' && (
                        <span className="inline-flex items-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={geocode.isPending}
                            onClick={() => geocode.mutate(p.id)}
                          >
                            {t('admin.salesPoints.geocodeOne')}
                          </Button>
                          <HelpTip k="sales_points.geocode_one" />
                        </span>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing({ point: p, locationId: p.locationId })}
                      >
                        {t('common.edit')}
                      </Button>
                      <ConfirmButton
                        message={t('admin.salesPoints.confirmDelete', { name: p.name })}
                        onConfirm={() => remove.mutateAsync(p.id)}
                      >
                        {t('common.delete')}
                      </ConfirmButton>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
          {manage && data.locations.some((l) => !l.used) && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-ink-2">{t('admin.salesPoints.fromLocation')}</span>
              {data.locations
                .filter((l) => !l.used)
                .map((l) => (
                  <Button key={l.id} size="sm" onClick={() => setEditing({ point: null, locationId: l.id })}>
                    {t('admin.salesPoints.addFromLocation', {
                      label: l.label ?? `${l.district ?? ''}/${l.city}`,
                    })}
                  </Button>
                ))}
              <HelpTip k="sales_points.from_location" />
            </div>
          )}
        </div>
      )}
      {editing && data && (
        <SalesPointDialog
          companyId={companyId}
          data={data}
          point={editing.point}
          locationId={editing.locationId}
          onClose={() => setEditing(null)}
        />
      )}
    </Section>
  );
}

function SalesPointDialog({
  companyId,
  data,
  point,
  locationId,
  onClose,
}: {
  companyId: string;
  data: CompanySalesPoints;
  point: SalesPointRow | null;
  locationId: string | null;
  onClose: () => void;
}) {
  const t = useT();
  const loc = locationId ? data.locations.find((l) => l.id === locationId) : null;
  const [d, setD] = useState<Draft>(() =>
    point
      ? draftOf(point)
      : {
          locationId,
          visible: !data.personNameRisk,
          name: data.company.name,
          city: loc?.city ?? '',
          district: loc?.district ?? '',
          neighbourhood: '',
          addressLine: loc?.line ?? '',
          postalCode: loc?.postalCode ?? '',
          pin: '',
          phone: '',
          hours: emptyHours(),
          note: '',
        },
  );
  const set = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));
  const neighbourhoods = useQuery({
    queryKey: ['neighbourhoods', d.city, d.district],
    queryFn: () =>
      api.get<{ items: string[] }>(
        `/v1/admin/geo/neighbourhoods?city=${encodeURIComponent(d.city)}&district=${encodeURIComponent(d.district)}`,
      ),
    enabled: !!d.city && !!d.district,
    staleTime: Infinity,
  });
  const pin = d.pin.trim() ? parseCoordinates(d.pin) : null;
  const pinInvalid = !!d.pin.trim() && !pin;
  const body = {
    locationId: d.locationId,
    visible: d.visible,
    name: d.name.trim(),
    city: d.city,
    district: d.district,
    neighbourhood: d.neighbourhood || null,
    addressLine: d.addressLine.trim() || null,
    postalCode: d.postalCode.trim() || null,
    latE6: pin?.latE6 ?? null,
    lngE6: pin?.lngE6 ?? null,
    phone: d.phone.trim() || null,
    hours: hoursBody(d.hours),
    note: d.note.trim() || null,
    ...(point ? { version: point.version } : {}),
  };
  const save = useApiMutation(
    () =>
      point
        ? api.put(`/v1/admin/companies/${companyId}/sales-points/${point.id}`, body)
        : api.post(`/v1/admin/companies/${companyId}/sales-points`, body),
    {
      invalidate: [['company-sales-points', companyId], ['sales-points']],
      success: t('admin.salesPoints.saved'),
      silentFieldErrors: true,
      onSuccess: onClose,
    },
  );
  const err = (f: string) => fieldError(save.error, f);
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    // Yalnızca adres (ad gönderilmez: şahıs firmasında kişi adı olabilir).
    [d.addressLine, d.neighbourhood, d.district, d.city].filter(Boolean).join(', '),
  )}`;
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={point ? t('admin.salesPoints.edit') : t('admin.salesPoints.add')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!d.name.trim() || !d.city || !d.district || pinInvalid}
            onClick={() => save.mutate(undefined)}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex items-center gap-2 sm:col-span-2">
          <Switch
            checked={d.visible}
            onCheckedChange={(v) => set({ visible: v })}
            label={t('admin.salesPoints.visible')}
          />
          <span className="text-sm">{t('admin.salesPoints.visible')}</span>
          <HelpTip k="sales_points.visible" />
        </div>
        <Field
          label={t('admin.salesPoints.name')}
          required
          help={<HelpTip k="sales_points.name" />}
          description={t('admin.salesPoints.nameLead')}
          error={err('name')}
          className="sm:col-span-2"
        >
          {(p) => (
            <Input {...p} maxLength={120} value={d.name} onChange={(e) => set({ name: e.target.value })} />
          )}
        </Field>
        <TrPlaceFields
          city={d.city}
          district={d.district}
          onChange={(v) => set({ ...v, neighbourhood: '' })}
          cityLabel={t('admin.dealers.city')}
          districtLabel={t('admin.dealers.district')}
          required
          districtRequired
          cityError={err('city')}
          districtError={err('district')}
        />
        <Field
          label={t('admin.salesPoints.neighbourhood')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="sales_points.neighbourhood" />}
          error={err('neighbourhood')}
        >
          {(p) => (
            <Select
              {...p}
              value={d.neighbourhood}
              disabled={!neighbourhoods.data?.items.length}
              onChange={(e) => set({ neighbourhood: e.target.value })}
            >
              <option value="">
                {d.district
                  ? t('admin.salesPoints.chooseNeighbourhood')
                  : t('admin.orders.chooseProvinceFirst')}
              </option>
              {neighbourhoods.data?.items.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label={t('admin.locations.postalCode')}
          optionalLabel={t('common.optional')}
          error={err('postalCode')}
        >
          {(p) => (
            <Input
              {...p}
              inputMode="numeric"
              maxLength={5}
              value={d.postalCode}
              onChange={(e) => set({ postalCode: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('admin.salesPoints.addressLine')}
          optionalLabel={t('common.optional')}
          error={err('addressLine')}
          className="sm:col-span-2"
        >
          {(p) => (
            <Input
              {...p}
              maxLength={300}
              value={d.addressLine}
              onChange={(e) => set({ addressLine: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('admin.salesPoints.pin')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="sales_points.pin" />}
          description={
            pin
              ? t('admin.salesPoints.pinRead', { lat: e6ToText(pin.latE6), lng: e6ToText(pin.lngE6) })
              : t('admin.salesPoints.pinLead')
          }
          error={pinInvalid ? t('admin.salesPoints.pinInvalid') : err('latE6')}
          className="sm:col-span-2"
        >
          {(p) => (
            <div className="flex flex-wrap gap-2">
              <Input
                {...p}
                className="min-w-0 flex-1"
                value={d.pin}
                placeholder="41.008200, 28.978400"
                onChange={(e) => set({ pin: e.target.value })}
              />
              <a
                className="inline-flex min-h-10 items-center rounded-md border border-line px-3 text-sm font-medium text-pine hover:bg-pine-tint"
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('admin.salesPoints.findOnMaps')}
              </a>
            </div>
          )}
        </Field>
        <Field
          label={t('admin.salesPoints.phone')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="sales_points.phone" />}
          description={t('admin.salesPoints.phoneLead')}
          error={err('phone')}
          className="sm:col-span-2"
        >
          {(p) => (
            <Input {...p} type="tel" value={d.phone} onChange={(e) => set({ phone: e.target.value })} />
          )}
        </Field>
      </div>
      <fieldset className="mt-4 space-y-2">
        <legend className="flex items-center gap-1.5 text-sm font-medium">
          {t('admin.salesPoints.hours')} <HelpTip k="sales_points.hours" />
        </legend>
        {WEEKDAYS.map((day) => {
          const v = d.hours[day];
          const setDay = (patch: Partial<Draft['hours'][Day]>) =>
            set({ hours: { ...d.hours, [day]: { ...v, ...patch } } });
          return (
            <div key={day} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="w-24">{t(`admin.salesPoints.days.${day}`)}</span>
              <Select
                aria-label={t('admin.salesPoints.dayMode', { day: t(`admin.salesPoints.days.${day}`) })}
                className="w-40"
                value={v.mode}
                onChange={(e) => setDay({ mode: e.target.value as 'unknown' | 'closed' | 'open' })}
              >
                <option value="unknown">{t('admin.salesPoints.dayUnknown')}</option>
                <option value="open">{t('admin.salesPoints.dayOpen')}</option>
                <option value="closed">{t('admin.salesPoints.dayClosed')}</option>
              </Select>
              {v.mode === 'open' && (
                <>
                  <Input
                    type="time"
                    className="w-28"
                    aria-label={t('admin.salesPoints.opens', { day: t(`admin.salesPoints.days.${day}`) })}
                    value={v.from}
                    onChange={(e) => setDay({ from: e.target.value })}
                  />
                  <span aria-hidden>–</span>
                  <Input
                    type="time"
                    className="w-28"
                    aria-label={t('admin.salesPoints.closes', { day: t(`admin.salesPoints.days.${day}`) })}
                    value={v.to}
                    onChange={(e) => setDay({ to: e.target.value })}
                  />
                </>
              )}
            </div>
          );
        })}
        {err('hours') && <p className="text-sm text-rose">{err('hours')}</p>}
      </fieldset>
      <Field
        label={t('admin.salesPoints.note')}
        optionalLabel={t('common.optional')}
        help={<HelpTip k="sales_points.note" />}
        error={err('note')}
        className="mt-4"
      >
        {(p) => (
          <Textarea
            {...p}
            rows={2}
            maxLength={300}
            value={d.note}
            onChange={(e) => set({ note: e.target.value })}
          />
        )}
      </Field>
    </Dialog>
  );
}
